import type { View } from "@perspective-dev/client";
import "@perspective-dev/viewer/dist/esm/perspective-viewer.inline.js";

import {
  SIGNAL_EVENTS_COLUMN_SLOTS,
  normalizePerspectiveColumns,
  normalizeSignalEventsData,
  resolveSignalEventsMapping,
  type EventSample,
  type NormalizeSignalEventsResult,
  type SignalEventsColumnMapping,
  type SignalEventsPluginConfig,
} from "./signal_events_overlay_data";

const PLUGIN_NAME = "Signal + Events Overlay";
const PLUGIN_ELEMENT_NAME = "playground-signal-events-overlay-plugin";

const MARGIN = {
  top: 16,
  right: 16,
  bottom: 28,
  left: 44,
};

type PerspectiveViewerLike = HTMLElement & {
  getViewConfig?: () => Promise<{ columns?: Array<string | null> }>;
};

type PerspectiveViewerClass = {
  registerPlugin: (name: string) => Promise<void>;
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

function formatAxisValue(value: number): string {
  if (!Number.isFinite(value)) {
    return "-";
  }

  if (Math.abs(value) >= 1_000_000) {
    return value.toExponential(2);
  }

  if (Math.abs(value) >= 1_000) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }

  if (Math.abs(value) >= 10) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function coercePluginConfig(config: unknown): SignalEventsPluginConfig {
  if (!config || typeof config !== "object") {
    return {};
  }

  const value = config as Record<string, unknown>;
  const readString = (key: string): string | undefined => {
    const candidate = value[key];
    return typeof candidate === "string" && candidate.trim() !== ""
      ? candidate
      : undefined;
  };

  return {
    timeColumn: readString("timeColumn"),
    signalColumn: readString("signalColumn"),
    eventColumn: readString("eventColumn"),
    eventYColumn: readString("eventYColumn"),
    eventLabelColumn: readString("eventLabelColumn"),
  };
}

function eventTooltip(event: EventSample, eventColumnName: string): string {
  const parts = [
    `time: ${formatAxisValue(event.x)}`,
    `value: ${formatAxisValue(event.y)}`,
  ];

  if (event.label) {
    parts.push(`${eventColumnName}: ${event.label}`);
  }

  return parts.join("\n");
}

function buildTickLines(width: number, height: number): string {
  const lines: string[] = [];

  for (let index = 0; index <= 4; index += 1) {
    const y = MARGIN.top + (index / 4) * (height - MARGIN.top - MARGIN.bottom);
    lines.push(`<line x1="${MARGIN.left}" y1="${y}" x2="${width - MARGIN.right}" y2="${y}" class="grid-line" />`);
  }

  return lines.join("");
}

function drawSeriesToSvg(
  width: number,
  height: number,
  data: NormalizeSignalEventsResult,
  mapping: SignalEventsColumnMapping
): string {
  const plotWidth = Math.max(1, width - MARGIN.left - MARGIN.right);
  const plotHeight = Math.max(1, height - MARGIN.top - MARGIN.bottom);

  const scaleX = (value: number): number => {
    const ratio = (value - data.xMin) / (data.xMax - data.xMin || 1);
    return MARGIN.left + clamp(ratio, 0, 1) * plotWidth;
  };

  const scaleY = (value: number): number => {
    const ratio = (value - data.yMin) / (data.yMax - data.yMin || 1);
    return MARGIN.top + (1 - clamp(ratio, 0, 1)) * plotHeight;
  };

  const linePath = data.signal
    .map((point, index) => `${index === 0 ? "M" : "L"} ${scaleX(point.x).toFixed(2)} ${scaleY(point.y).toFixed(2)}`)
    .join(" ");

  const events = data.events
    .map((eventPoint) => {
      const x = scaleX(eventPoint.x).toFixed(2);
      const y = scaleY(eventPoint.y).toFixed(2);
      const title = escapeHtml(eventTooltip(eventPoint, mapping.eventLabelColumn ?? mapping.eventColumn));
      return `<circle class="event-point" cx="${x}" cy="${y}" r="3.2"><title>${title}</title></circle>`;
    })
    .join("");

  const lineTitle = escapeHtml(
    `${mapping.signalColumn}: continuous signal. Hover orange points for matched events.`
  );

  return `
    <rect class="plot-surface" x="${MARGIN.left}" y="${MARGIN.top}" width="${plotWidth}" height="${plotHeight}" />
    <g class="grid-layer">${buildTickLines(width, height)}</g>
    <line class="axis" x1="${MARGIN.left}" y1="${height - MARGIN.bottom}" x2="${width - MARGIN.right}" y2="${height - MARGIN.bottom}" />
    <line class="axis" x1="${MARGIN.left}" y1="${MARGIN.top}" x2="${MARGIN.left}" y2="${height - MARGIN.bottom}" />
    <path class="signal-line" d="${linePath}"><title>${lineTitle}</title></path>
    <g class="events-layer">${events}</g>
    <text class="axis-label axis-label-x" x="${width - MARGIN.right}" y="${height - 8}" text-anchor="end">${escapeHtml(mapping.timeColumn)}</text>
    <text class="axis-label axis-label-y" x="${MARGIN.left + 4}" y="${MARGIN.top + 12}" text-anchor="start">${escapeHtml(mapping.signalColumn)}</text>
    <text class="axis-value axis-value-min" x="${MARGIN.left}" y="${height - 8}" text-anchor="start">${escapeHtml(formatAxisValue(data.xMin))}</text>
    <text class="axis-value axis-value-max" x="${width - MARGIN.right}" y="${height - 8}" text-anchor="end">${escapeHtml(formatAxisValue(data.xMax))}</text>
  `;
}

function createPluginClass() {
  const BasePlugin = customElements.get("perspective-viewer-plugin") as
    | (new () => HTMLElement)
    | undefined;

  if (!BasePlugin) {
    throw new Error("perspective-viewer-plugin element is not registered");
  }

  return class SignalEventsOverlayPluginElement extends BasePlugin {
    private initialized = false;
    private svgElement: SVGSVGElement | null = null;
    private statusElement: HTMLDivElement | null = null;
    private stagedView: View | null = null;
    private lastData: NormalizeSignalEventsResult | null = null;
    private lastMapping: SignalEventsColumnMapping | null = null;
    private pluginConfig: SignalEventsPluginConfig = {};

    get name(): string {
      return PLUGIN_NAME;
    }

    get select_mode(): string {
      return "toggle";
    }

    get min_config_columns(): number {
      return 3;
    }

    get config_column_names(): string[] {
      return [...SIGNAL_EVENTS_COLUMN_SLOTS];
    }

    get priority(): number {
      return -100;
    }

    connectedCallback(): void {
      if (this.initialized) {
        return;
      }

      this.attachShadow({ mode: "open" });
      this.shadowRoot!.innerHTML = `
        <style>
          :host {
            display: block;
            position: absolute;
            inset: 0;
            color: #334155;
            font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;
          }

          .root {
            position: absolute;
            inset: 0;
            overflow: hidden;
            background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          }

          svg {
            width: 100%;
            height: 100%;
            display: block;
          }

          .plot-surface {
            fill: rgba(148, 163, 184, 0.06);
          }

          .grid-line {
            stroke: rgba(148, 163, 184, 0.35);
            stroke-width: 1;
            shape-rendering: crispEdges;
          }

          .axis {
            stroke: rgba(71, 85, 105, 0.55);
            stroke-width: 1;
            shape-rendering: crispEdges;
          }

          .signal-line {
            fill: none;
            stroke: #0284c7;
            stroke-width: 1.8;
            stroke-linecap: round;
            stroke-linejoin: round;
          }

          .event-point {
            fill: #f97316;
            stroke: #ffffff;
            stroke-width: 1.2;
          }

          .axis-label {
            fill: rgba(51, 65, 85, 0.9);
            font-size: 11px;
            font-weight: 600;
          }

          .axis-value {
            fill: rgba(71, 85, 105, 0.7);
            font-size: 10px;
            font-weight: 500;
          }

          .status {
            position: absolute;
            left: 10px;
            top: 8px;
            max-width: calc(100% - 20px);
            font-size: 11px;
            color: rgba(51, 65, 85, 0.92);
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
            pointer-events: none;
            text-shadow: 0 1px 0 rgba(255, 255, 255, 0.7);
          }

          .status.error {
            color: #b91c1c;
          }
        </style>
        <div class="root">
          <svg part="chart" aria-label="Signal and events chart" preserveAspectRatio="none"></svg>
          <div class="status" part="status"></div>
        </div>
      `;

      this.svgElement = this.shadowRoot!.querySelector("svg");
      this.statusElement = this.shadowRoot!.querySelector(".status");
      this.initialized = true;
    }

    async draw(view: View): Promise<void> {
      await this.renderView(view);
    }

    async update(view: View): Promise<void> {
      await this.renderView(view);
    }

    async resize(): Promise<void> {
      if (this.offsetParent === null) {
        return;
      }

      if (this.stagedView) {
        const view = this.stagedView;
        this.stagedView = null;
        await this.renderView(view);
        return;
      }

      if (this.lastData && this.lastMapping) {
        this.renderChart(this.lastData, this.lastMapping);
      }
    }

    async restyle(): Promise<void> {
      if (this.lastData && this.lastMapping) {
        this.renderChart(this.lastData, this.lastMapping);
      }
    }

    async clear(): Promise<void> {
      if (this.svgElement) {
        this.svgElement.innerHTML = "";
      }
      this.lastData = null;
      this.lastMapping = null;
      this.setStatus("", false);
    }

    async save(): Promise<SignalEventsPluginConfig> {
      return this.pluginConfig;
    }

    async restore(config: unknown): Promise<void> {
      this.pluginConfig = coercePluginConfig(config);
      if (this.stagedView) {
        await this.renderView(this.stagedView);
      }
    }

    async delete(): Promise<void> {
      await this.clear();
      this.stagedView = null;
    }

    private async renderView(view: View): Promise<void> {
      if (!this.initialized) {
        this.connectedCallback();
      }

      if (this.offsetParent === null) {
        this.stagedView = view;
        return;
      }

      const viewer = this.parentElement as PerspectiveViewerLike | null;
      if (!viewer || typeof viewer.getViewConfig !== "function") {
        this.setStatus("Viewer config unavailable.", true);
        return;
      }

      try {
        const [columnsPayload, viewConfig] = await Promise.all([
          view.to_columns(),
          viewer.getViewConfig(),
        ]);

        const normalizedColumns = normalizePerspectiveColumns(columnsPayload);
        const availableColumns = Object.keys(normalizedColumns);

        const resolved = resolveSignalEventsMapping({
          availableColumns,
          configuredColumns: viewConfig.columns,
          pluginConfig: this.pluginConfig,
        });

        if (!resolved.ok) {
          this.lastData = null;
          this.lastMapping = null;
          if (this.svgElement) {
            this.svgElement.innerHTML = "";
          }
          this.setStatus(resolved.error, true);
          return;
        }

        const normalizedData = normalizeSignalEventsData(
          normalizedColumns,
          resolved.mapping
        );

        this.lastData = normalizedData;
        this.lastMapping = resolved.mapping;
        this.renderChart(normalizedData, resolved.mapping);

        if (normalizedData.signal.length === 0) {
          this.setStatus("No valid rows available for the current column mapping.", true);
          return;
        }

        const statusBits = [
          `${normalizedData.signal.length.toLocaleString()} samples`,
          `${normalizedData.events.length.toLocaleString()} events`,
        ];

        const warning = [...resolved.warnings, ...normalizedData.warnings][0];
        if (warning) {
          statusBits.push(warning);
        }

        this.setStatus(statusBits.join(" | "), false);
      } catch (error) {
        this.lastData = null;
        this.lastMapping = null;
        if (this.svgElement) {
          this.svgElement.innerHTML = "";
        }
        const message = error instanceof Error ? error.message : String(error);
        this.setStatus(`Render failed: ${message}`, true);
      }
    }

    private renderChart(
      data: NormalizeSignalEventsResult,
      mapping: SignalEventsColumnMapping
    ): void {
      if (!this.svgElement) {
        return;
      }

      const rect = this.getBoundingClientRect();
      const width = Math.max(280, Math.floor(rect.width || this.clientWidth || 700));
      const height = Math.max(180, Math.floor(rect.height || this.clientHeight || 320));

      this.svgElement.setAttribute("viewBox", `0 0 ${width} ${height}`);
      this.svgElement.innerHTML = drawSeriesToSvg(width, height, data, mapping);
    }

    private setStatus(message: string, isError: boolean): void {
      if (!this.statusElement) {
        return;
      }

      this.statusElement.textContent = message;
      this.statusElement.classList.toggle("error", isError && message.length > 0);
    }
  };
}

let registrationPromise: Promise<void> | null = null;

export function registerSignalEventsOverlayPlugin(): Promise<void> {
  if (registrationPromise) {
    return registrationPromise;
  }

  registrationPromise = (async () => {
    await customElements.whenDefined("perspective-viewer-plugin");

    if (!customElements.get(PLUGIN_ELEMENT_NAME)) {
      customElements.define(PLUGIN_ELEMENT_NAME, createPluginClass());
    }

    await customElements.whenDefined("perspective-viewer");

    const Viewer = customElements.get("perspective-viewer") as PerspectiveViewerClass | undefined;
    if (!Viewer || typeof Viewer.registerPlugin !== "function") {
      throw new Error("perspective-viewer registerPlugin() is unavailable");
    }

    try {
      await Viewer.registerPlugin(PLUGIN_ELEMENT_NAME);
    } catch (error) {
      const message = String(error ?? "").toLowerCase();
      if (!message.includes("already")) {
        throw error;
      }
    }
  })();

  registrationPromise.catch((error) => {
    console.warn("[playground-signal-events-overlay] plugin registration failed:", error);
  });

  return registrationPromise;
}

void registerSignalEventsOverlayPlugin();
